import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  EmployeeAvatar,
  EmptyState,
  Icon,
  IconBtn,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Spinner,
  Tabs,
  useToast,
} from "../../design-system";
import {
  createContract,
  createHoliday,
  createHolidayGroup,
  createWorkSchedule,
  getContractTerms,
  getEmployees,
  getEmployeeActiveContract,
  getHolidayGroups,
  getVacationPolicies,
  getWorkSchedules,
} from "../../helpers/kronaly_helper";
import AddTermsModal from "../../Components/Kronaly/Contratos/AddTermsModal";
import EditContractModal from "../../Components/Kronaly/Contratos/EditContractModal";
import TerminateContractModal from "../../Components/Kronaly/Contratos/TerminateContractModal";
import WorkScheduleEditor, {
  buildConfigPayload,
  buildEmptyConfig,
} from "../../Components/Kronaly/Contratos/WorkScheduleEditor";
import { CHANGE_TYPE_LABEL, formatMoney } from "../../Components/Kronaly/Contratos/formatters";
import VacationPolicyEditorModal from "../../Components/Kronaly/Vacaciones/VacationPolicyEditorModal";
import {
  ACCRUAL_UNITS as VAC_ACCRUAL_UNITS,
  DAY_COUNT_BASES as VAC_DAY_COUNT_BASES,
  ACCRUAL_MODES as VAC_ACCRUAL_MODES,
  MONTHS as VAC_MONTHS,
  summarizePolicy,
} from "../../Components/Kronaly/Vacaciones/vacationPolicyHelpers";

const KIND_LABEL = {
  national: "Nacional",
  regional: "Autonómico",
  local: "Local",
  other: "Otro",
};

const KIND_TONE = {
  national: "info",
  regional: "brand",
  local: "warning",
  other: "neutral",
};

function unwrap(r) {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
}

function fmtDate(ymd) {
  if (!ymd) return null;
  // Fecha-solo: parseo con componentes LOCALES (independiente de zona horaria).
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fullName(e) {
  if (!e) return "—";
  return `${e.first_name || ""} ${e.last_name || ""}`.trim() || "(sin nombre)";
}

function daysUntil(ymd) {
  if (!ymd) return null;
  const end = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  return Math.floor((end - now) / (1000 * 60 * 60 * 24));
}

function contractEstado(contract) {
  if (!contract?.end_date) return { label: "Activo", tone: "success", key: "active" };
  const days = daysUntil(contract.end_date);
  if (days == null) return { label: "Activo", tone: "success", key: "active" };
  if (days < 0) return { label: "Vencido", tone: "danger", key: "ended" };
  if (days <= 30) return { label: "Vence pronto", tone: "warning", key: "ending-soon" };
  return { label: "Activo", tone: "success", key: "active" };
}

function Contratos() {
  document.title = "Contratos | Kronaly";
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [activeContracts, setActiveContracts] = useState({});
  const [catalogues, setCatalogues] = useState({ schedules: [], holidays: [], policies: [] });
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [openEdit, setOpenEdit] = useState(null);
  const [openTerms, setOpenTerms] = useState(null);
  const [openTerminate, setOpenTerminate] = useState(null);

  const [tab, setTab] = useState("activos");
  const [search, setSearch] = useState("");

  const indexedSchedules = useMemo(() => {
    const m = {};
    catalogues.schedules.forEach((s) => { m[s.id] = s; });
    return m;
  }, [catalogues.schedules]);
  const indexedHolidays = useMemo(() => {
    const m = {};
    catalogues.holidays.forEach((h) => { m[h.id] = h; });
    return m;
  }, [catalogues.holidays]);
  const indexedPolicies = useMemo(() => {
    const m = {};
    catalogues.policies.forEach((p) => { m[p.id] = p; });
    return m;
  }, [catalogues.policies]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [emps, scheds, hols, vacs] = await Promise.all([
        getEmployees(),
        getWorkSchedules(),
        getHolidayGroups(),
        getVacationPolicies(),
      ]);
      const employeesList = unwrap(emps) || [];
      setEmployees(Array.isArray(employeesList) ? employeesList : []);
      setCatalogues({
        schedules: unwrap(scheds) || [],
        holidays: unwrap(hols) || [],
        policies: unwrap(vacs) || [],
      });

      const contracts = {};
      await Promise.all(
        (employeesList || []).map(async (e) => {
          try {
            const r = await getEmployeeActiveContract(e.id);
            const data = unwrap(r);
            if (data && (data.contract || data.id)) contracts[e.id] = data;
          } catch (_) {
            // ignore individual errors
          }
        })
      );
      setActiveContracts(contracts);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los contratos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const preselected = location.state?.openContractFor;
    if (preselected) {
      setEditing({ employee_profile_id: preselected });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const onCatalogueAdded = (kind, item) => {
    setCatalogues((c) => ({ ...c, [kind]: [...c[kind], item] }));
  };

  const allRows = useMemo(() => {
    return employees
      .map((e) => {
        const ac = activeContracts[e.id];
        if (!ac) return null;
        const contract = ac.contract || ac;
        const terms = ac.current_terms || ac.terms || ac.current || null;
        const schedule = terms?.work_schedule_id ? indexedSchedules[terms.work_schedule_id] : null;
        const policy = terms?.vacation_policy_id ? indexedPolicies[terms.vacation_policy_id] : null;
        const holidayIds = Array.isArray(terms?.holiday_group_ids) ? terms.holiday_group_ids : [];
        const holidayList = holidayIds.map((id) => indexedHolidays[id]).filter(Boolean);
        return {
          employee: e,
          contract,
          terms,
          schedule,
          policy,
          holidayList,
          estado: contractEstado(contract),
        };
      })
      .filter(Boolean);
  }, [employees, activeContracts, indexedSchedules, indexedPolicies, indexedHolidays]);

  const employeesWithoutContract = useMemo(
    () =>
      employees.filter((e) => !activeContracts[e.id]),
    [employees, activeContracts],
  );

  const counts = useMemo(() => {
    let activos = 0;
    let vence = 0;
    allRows.forEach((r) => {
      if (r.estado.key === "ending-soon") vence += 1;
      else activos += 1;
    });
    return { activos, vence, sinContrato: employeesWithoutContract.length };
  }, [allRows, employeesWithoutContract]);

  const filteredEmployeesWithoutContract = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employeesWithoutContract;
    return employeesWithoutContract.filter((e) => {
      const haystack = [fullName(e), e.position, e.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [employeesWithoutContract, search]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (tab === "vence" && r.estado.key !== "ending-soon") return false;
      if (tab === "activos" && r.estado.key === "ending-soon") {
        // los que vencen pronto siguen siendo activos: incluirlos
      }
      if (q) {
        const haystack = [
          fullName(r.employee),
          r.employee.position,
          r.employee.email,
          r.contract.id,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allRows, tab, search]);

  const refreshActive = async (employeeId) => {
    try {
      const r = await getEmployeeActiveContract(employeeId);
      const data = unwrap(r);
      setActiveContracts((prev) => ({ ...prev, [employeeId]: data || null }));
      if (viewing?.employee?.id === employeeId) {
        const contract = data?.contract || data;
        const terms = data?.current_terms || data?.terms || null;
        const holidayIds = Array.isArray(terms?.holiday_group_ids) ? terms.holiday_group_ids : [];
        setViewing((v) => v ? { ...v, contract, terms,
          schedule: terms?.work_schedule_id ? indexedSchedules[terms.work_schedule_id] : null,
          policy: terms?.vacation_policy_id ? indexedPolicies[terms.vacation_policy_id] : null,
          holidayList: holidayIds.map((id) => indexedHolidays[id]).filter(Boolean),
          estado: contractEstado(contract),
        } : v);
      }
    } catch (_) {
      // ignore
    }
  };

  return (
    <div className="kr-page">
      <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 4 }}>Equipo</div>
            <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>Contratos</h1>
            <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)", maxWidth: 640 }}>
              Los contratos definen las reglas de jornada, vacaciones y festivos para cada empleado. Toda modificación queda registrada en el histórico.
            </p>
          </div>
          <Button variant="primary" icon="Plus" onClick={() => setEditing({})}>Nuevo contrato</Button>
        </div>

        <Card style={{ padding: 16, background: "var(--kr-action-soft-bg)", border: "1px solid var(--kr-action-soft-bg)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="FileText" size={16} style={{ color: "var(--kr-action)" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>Los contratos son la base de las reglas de jornada</div>
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>Cualquier cambio aquí afecta horas semanales, días de vacaciones, festivos aplicables y cómo se generan los reportes mensuales.</div>
            </div>
          </div>
        </Card>

        {error && <Alert tone="danger">{error}</Alert>}

        <Card padded={false}>
          <div style={{ padding: "12px 16px 0" }}>
            <Tabs
              tabs={[
                { id: "activos", label: "Activos", count: counts.activos + counts.vence },
                { id: "vence", label: "Vencen pronto", count: counts.vence },
                { id: "sin-contrato", label: "Sin contrato", count: counts.sinContrato },
              ]}
              value={tab}
              onChange={setTab}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--kr-border-subtle)" }}>
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por empleado…"
              style={{ flex: 1, maxWidth: 320 }}
            />
            <div style={{ flex: 1 }} />
          </div>

          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <Spinner />
            </div>
          ) : tab === "sin-contrato" ? (
            filteredEmployeesWithoutContract.length === 0 ? (
              <div style={{ padding: 24 }}>
                <EmptyState
                  icon="UserCheck"
                  title={
                    employeesWithoutContract.length === 0
                      ? "Todos los empleados tienen contrato"
                      : "Ningún empleado coincide con el filtro"
                  }
                  description={
                    employeesWithoutContract.length === 0
                      ? "Buen trabajo. Cuando crees nuevos empleados aparecerán aquí hasta que les asignes un contrato."
                      : "Prueba con otro nombre o limpia la búsqueda."
                  }
                />
              </div>
            ) : (
              <EmployeesWithoutContractList
                employees={filteredEmployeesWithoutContract}
                onCreateForEmployee={(empId) => setEditing({ employee_profile_id: empId })}
              />
            )
          ) : filteredRows.length === 0 ? (
            <div style={{ padding: 24 }}>
              <EmptyState
                icon="Briefcase"
                title={
                  allRows.length === 0
                    ? "Aún no hay contratos activos"
                    : "Ningún contrato coincide con el filtro"
                }
                description={
                  allRows.length === 0
                    ? employees.length === 0
                      ? "Primero crea un empleado en Equipo › Empleados, luego asígnale un contrato."
                      : "Crea el primer contrato para asignar horario y políticas a un empleado."
                    : "Prueba con otra búsqueda o cambia de pestaña."
                }
                action={
                  allRows.length === 0 && employees.length > 0 ? (
                    <Button variant="primary" icon="Plus" onClick={() => setEditing({})}>Nuevo contrato</Button>
                  ) : null
                }
              />
            </div>
          ) : (
            <ContractsTable rows={filteredRows} onOpen={(row) => setViewing(row)} />
          )}
        </Card>
      </div>

      <Drawer
        open={!!viewing}
        onClose={() => setViewing(null)}
        size="xl"
        title={
          viewing ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <EmployeeAvatar employeeId={viewing.employee.id} hasAvatar={true} name={fullName(viewing.employee)} size={36} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Contrato</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {fullName(viewing.employee)}
                </div>
              </div>
              <Badge tone={viewing.estado.tone} dot style={{ marginLeft: 8 }}>{viewing.estado.label}</Badge>
            </div>
          ) : null
        }
        footer={
          viewing ? (
            <>
              <Link to={`/empleados/${viewing.employee.id}`} style={{ textDecoration: "none" }}>
                <Button variant="ghost" icon="User">Ver perfil</Button>
              </Link>
              <div style={{ flex: 1 }} />
              <Button variant="soft" icon="Settings" onClick={() => setOpenEdit(viewing.contract)}>
                Editar
              </Button>
              {!viewing.contract.end_date && (
                <Button variant="dangerSoft" icon="XCircle" onClick={() => setOpenTerminate(viewing.contract)}>
                  Terminar contrato
                </Button>
              )}
              <Button variant="primary" icon="Plus" onClick={() => setOpenTerms(viewing.contract)}>
                Agregar anexo
              </Button>
            </>
          ) : null
        }
      >
        {viewing && (
          <ContractDetail
            row={viewing}
            schedulesById={indexedSchedules}
            holidaysById={indexedHolidays}
            policiesById={indexedPolicies}
          />
        )}
      </Drawer>

      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        size="xl"
        title={
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Nuevo contrato</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)" }}>Asistente de creación</div>
          </div>
        }
      >
        {editing && (
          <ContratoEditor
            initial={editing}
            employees={employees}
            catalogues={catalogues}
            onCatalogueAdded={onCatalogueAdded}
            onClose={() => setEditing(null)}
            onCreated={async () => {
              setEditing(null);
              await load();
            }}
          />
        )}
      </Drawer>

      <EditContractModal
        open={!!openEdit}
        contract={openEdit}
        onClose={() => setOpenEdit(null)}
        onUpdated={async () => {
          if (viewing?.employee?.id) await refreshActive(viewing.employee.id);
        }}
      />
      <AddTermsModal
        open={!!openTerms}
        contractId={openTerms?.id}
        onClose={() => setOpenTerms(null)}
        onCreated={async () => {
          if (viewing?.employee?.id) await refreshActive(viewing.employee.id);
        }}
      />
      <TerminateContractModal
        open={!!openTerminate}
        contract={openTerminate}
        onClose={() => setOpenTerminate(null)}
        onTerminated={async () => {
          if (viewing?.employee?.id) await refreshActive(viewing.employee.id);
        }}
      />
    </div>
  );
}

function EmployeesWithoutContractList({ employees, onCreateForEmployee }) {
  const cols = "2fr 1.6fr 1.4fr auto";
  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: cols,
          gap: 12,
          padding: "10px 20px",
          background: "var(--kr-neutral-50)",
          borderBottom: "1px solid var(--kr-border)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          color: "var(--kr-fg-muted)",
        }}
      >
        <span>Empleado</span>
        <span>Email</span>
        <span>Cargo</span>
        <span style={{ textAlign: "right" }}></span>
      </div>
      {employees.map((e, i) => (
        <EmployeeWithoutContractRow
          key={e.id}
          employee={e}
          cols={cols}
          last={i === employees.length - 1}
          onCreate={() => onCreateForEmployee(e.id)}
        />
      ))}
    </div>
  );
}

function EmployeeWithoutContractRow({ employee, cols, last, onCreate }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onCreate}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCreate();
        }
      }}
      style={{
        display: "grid",
        gridTemplateColumns: cols,
        gap: 12,
        alignItems: "center",
        padding: "12px 20px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "#fff",
        cursor: "pointer",
        fontSize: 13,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <EmployeeAvatar employeeId={employee.id} hasAvatar={true} name={fullName(employee)} size={28} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "var(--kr-fg-strong)",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {fullName(employee)}
          </div>
        </div>
      </div>
      <div
        style={{
          color: "var(--kr-fg-muted)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {employee.email || "—"}
      </div>
      <div style={{ color: "var(--kr-fg-strong)" }}>{employee.position || "—"}</div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="primary"
          size="sm"
          icon="Plus"
          onClick={(e) => {
            e.stopPropagation();
            onCreate();
          }}
        >
          Crear contrato
        </Button>
      </div>
    </div>
  );
}

function ContractsTable({ rows, onOpen }) {
  const cols = "1.6fr 1fr 1fr 1fr 0.7fr 1.1fr 1fr 0.7fr";
  return (
    <div>
      <div style={{
        display: "grid", gridTemplateColumns: cols, gap: 12,
        padding: "10px 20px", background: "var(--kr-neutral-50)",
        borderBottom: "1px solid var(--kr-border)",
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em",
        fontWeight: 600, color: "var(--kr-fg-muted)",
      }}>
        <span>Empleado</span>
        <span>Tipo</span>
        <span>Inicio</span>
        <span>Fin</span>
        <span>h/sem</span>
        <span>Vacaciones</span>
        <span>Horario</span>
        <span style={{ textAlign: "right" }}>Estado</span>
      </div>
      {rows.map((r, i) => (
        <ContractRow
          key={r.contract.id}
          row={r}
          cols={cols}
          last={i === rows.length - 1}
          onOpen={() => onOpen(r)}
        />
      ))}
    </div>
  );
}

function ContractRow({ row, cols, last, onOpen }) {
  const { employee, contract, schedule, policy, estado } = row;
  const [hover, setHover] = useState(false);
  const tipo = contract.end_date ? "Temporal" : "Indefinido";
  const tipoColor = contract.end_date ? "warning" : "info";
  const weekly = schedule?.weekly_minutes ? Math.round((schedule.weekly_minutes / 60) * 10) / 10 : "—";

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      style={{
        display: "grid", gridTemplateColumns: cols, gap: 12,
        alignItems: "center", padding: "12px 20px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "#fff",
        cursor: "pointer", fontSize: 13,
        transition: "background var(--kr-dur-fast) var(--kr-ease-out)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <EmployeeAvatar employeeId={employee.id} hasAvatar={true} name={fullName(employee)} size={28} />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fullName(employee)}</div>
          <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>{employee.position || ""}</div>
        </div>
      </div>
      <div><Badge tone={tipoColor}>{tipo}</Badge></div>
      <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", fontSize: 12 }}>{fmtDate(contract.start_date)}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", color: contract.end_date ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)", fontSize: 12 }}>{fmtDate(contract.end_date) || "—"}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontVariantNumeric: "tabular-nums", color: "var(--kr-fg-strong)", fontWeight: 600 }}>{weekly}</div>
      <div style={{ color: "var(--kr-fg-strong)", fontSize: 12 }}>{policy?.name || "—"}</div>
      <div style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>{schedule?.name || "—"}</div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Badge tone={estado.tone} dot>{estado.label}</Badge>
      </div>
    </div>
  );
}

function ContractDetail({ row, schedulesById, holidaysById, policiesById }) {
  const { employee, contract, terms, schedule, policy, holidayList } = row;

  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setHistory(null);
    setHistoryError(null);
    setHistoryLoading(true);
    getContractTerms(contract.id)
      .then((r) => { if (!cancelled) setHistory(unwrap(r) || []); })
      .catch((e) => { if (!cancelled) setHistoryError(typeof e === "string" ? e : e?.message || "No se pudo cargar el histórico"); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [contract.id]);

  const weekly = schedule?.weekly_minutes ? (schedule.weekly_minutes / 60).toFixed(1) : "—";

  const empleadoRows = [
    ["Nombre", fullName(employee)],
    ["Puesto", terms?.job_title || employee.position || "—"],
    ["Email", employee.email || "—"],
  ];
  const periodoRows = [
    ["Inicio", fmtDate(contract.start_date) || "—"],
    ["Fin", contract.end_date ? fmtDate(contract.end_date) : "Indefinido"],
    ["Salario", formatMoney(terms?.salary_amount, terms?.salary_currency) || "—"],
  ];
  const jornadaRows = [
    ["Horario", schedule?.name || "—"],
    ["h/sem", weekly],
  ];
  const holidaysLabel = holidayList && holidayList.length > 0
    ? holidayList.map((h) => h.name).join(" · ")
    : "—";
  const reglasRows = [
    ["Vacaciones", policy?.name || "—"],
    ["Festivos", holidaysLabel],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <SummaryBlock title="Empleado" rows={empleadoRows} />
        <SummaryBlock title="Periodo" rows={periodoRows} />
        <SummaryBlock title="Jornada" rows={jornadaRows} />
        <SummaryBlock title="Reglas" rows={reglasRows} />
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 8 }}>Historial de términos</div>
        {historyLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 16 }}><Spinner size={16} /></div>
        ) : historyError ? (
          <Alert tone="danger">{historyError}</Alert>
        ) : !history || history.length === 0 ? (
          <Card padded>
            <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Sin términos registrados todavía.</div>
          </Card>
        ) : (
          <Card padded={false}>
            {history.map((t, i) => (
              <TermsRow
                key={t.id}
                term={t}
                schedule={schedulesById[t.work_schedule_id]}
                holidays={(t.holiday_group_ids || []).map((gid) => holidaysById[gid]).filter(Boolean)}
                policy={policiesById[t.vacation_policy_id]}
                last={i === history.length - 1}
              />
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

function TermsRow({ term, schedule, holidays, policy, last }) {
  return (
    <div style={{
      padding: "12px 16px",
      borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
      fontSize: 12,
    }}>
      <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", fontWeight: 600 }}>
          {fmtDate(term.effective_from) || "—"}
          {" → "}
          {term.effective_to ? fmtDate(term.effective_to) : <span style={{ color: "var(--kr-fg-muted)" }}>vigente</span>}
        </div>
        <Badge tone={term.effective_to ? "neutral" : "success"}>
          {CHANGE_TYPE_LABEL[term.change_type] || term.change_type || "—"}
        </Badge>
      </div>
      {term.job_title && <Cell label="Puesto" value={term.job_title} />}
      {term.salary_amount != null && <Cell label="Salario" value={formatMoney(term.salary_amount, term.salary_currency)} />}
      {schedule && <Cell label="Horario" value={schedule.name} />}
      {holidays && holidays.length > 0 && (
        <Cell label="Festivos" value={holidays.map((h) => h.name).join(" · ")} />
      )}
      {policy && <Cell label="Vacaciones" value={policy.name} />}
      {term.note && <Cell label="Nota" value={term.note} full />}
    </div>
  );
}

function Cell({ label, value, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto", display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span style={{ color: "var(--kr-fg-muted)" }}>{label}</span>
      <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500, textAlign: "right" }}>{value}</span>
    </div>
  );
}

function SummaryBlock({ title, rows }) {
  return (
    <Card padded={true}>
      <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 10 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
            <span style={{ color: "var(--kr-fg-muted)" }}>{k}</span>
            <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>{v}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ContratoEditor({ initial, employees, catalogues, onCatalogueAdded, onClose, onCreated }) {
  const toast = useToast();
  const [step, setStep] = useState(initial?.employee_profile_id ? 1 : 0);
  const [form, setForm] = useState({
    employee_profile_id: initial?.employee_profile_id || "",
    start_date: todayYmd(),
    end_date: "",
    work_schedule_id: "",
    holiday_group_ids: [],
    vacation_policy_id: "",
    job_title: "",
    salary_amount: "",
    salary_currency: "EUR",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const selectedEmployee = employees.find((e) => e.id === form.employee_profile_id) || null;
  const selectedSchedule = catalogues.schedules.find((s) => s.id === form.work_schedule_id) || null;
  const selectedHolidays = catalogues.holidays.filter((h) => form.holiday_group_ids.includes(h.id));
  const selectedPolicy = catalogues.policies.find((p) => p.id === form.vacation_policy_id) || null;

  const steps = [
    { label: "Empleado", icon: "Users", isValid: () => !!form.employee_profile_id },
    { label: "Periodo y jornada", icon: "Calendar", isValid: () => !!form.start_date && !!form.work_schedule_id },
    { label: "Reglas y políticas", icon: "Settings", isValid: () => form.holiday_group_ids.length > 0 && !!form.vacation_policy_id },
    { label: "Documentos", icon: "FileText", isValid: () => true },
    { label: "Resumen", icon: "CheckCircle2", isValid: () => true },
  ];

  const canSubmit =
    !!form.employee_profile_id &&
    !!form.start_date &&
    !!form.work_schedule_id &&
    form.holiday_group_ids.length > 0 &&
    !!form.vacation_policy_id &&
    !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        employee_profile_id: form.employee_profile_id,
        start_date: form.start_date,
        work_schedule_id: form.work_schedule_id,
        holiday_group_ids: form.holiday_group_ids,
        vacation_policy_id: form.vacation_policy_id,
      };
      if (form.end_date) payload.end_date = form.end_date;
      if (form.job_title.trim()) payload.job_title = form.job_title.trim();
      if (form.salary_amount !== "" && !Number.isNaN(Number(form.salary_amount))) {
        payload.salary_amount = Number(form.salary_amount);
        payload.salary_currency = form.salary_currency || "EUR";
      }
      await createContract(payload);
      toast.success(
        "Contrato creado",
        selectedEmployee ? `${fullName(selectedEmployee)} ya tiene contrato vigente.` : undefined
      );
      onCreated();
    } catch (e) {
      const msg = typeof e === "string" ? e : e?.message || "No se pudo crear el contrato";
      setError(msg);
      toast.error("No se pudo crear el contrato", msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", margin: -20 }}>
      <div style={{ display: "flex", padding: "16px 20px", gap: 8, borderBottom: "1px solid var(--kr-border-subtle)", background: "var(--kr-neutral-50)" }}>
        {steps.map((s, i) => (
          <div key={i} onClick={() => setStep(i)}
            style={{
              flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
              background: step === i ? "#fff" : "transparent", borderRadius: 6,
              border: step === i ? "1px solid var(--kr-border)" : "1px solid transparent",
              cursor: "pointer",
            }}>
            <div style={{
              width: 22, height: 22, borderRadius: 999,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: step >= i ? "var(--kr-action)" : "var(--kr-neutral-150)",
              color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "var(--kr-font-mono)",
            }}>{step > i ? <Icon name="Check" size={12} /> : i + 1}</div>
            <span style={{ fontSize: 12, fontWeight: step === i ? 600 : 500, color: step === i ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)" }}>{s.label}</span>
          </div>
        ))}
      </div>

      {step > 0 && selectedEmployee && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderBottom: "1px solid var(--kr-border-subtle)",
            background: "var(--kr-action-soft-bg)",
          }}
        >
          <EmployeeAvatar employeeId={selectedEmployee.id} hasAvatar={true} name={fullName(selectedEmployee)} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--kr-fg-muted)",
                fontWeight: 600,
              }}
            >
              Contrato para
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--kr-fg-strong)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {fullName(selectedEmployee)}
            </div>
            {selectedEmployee.position && (
              <div
                style={{
                  fontSize: 11,
                  color: "var(--kr-fg-muted)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedEmployee.position}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ padding: 24, flex: 1, overflow: "auto" }}>
        {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></div>}
        {step === 0 && <StepEmpleado employees={employees} value={form.employee_profile_id} onChange={(v) => update({ employee_profile_id: v })} />}
        {step === 1 && (
          <StepPeriodo
            form={form}
            update={update}
            schedules={catalogues.schedules}
            selectedSchedule={selectedSchedule}
            onCatalogueAdded={onCatalogueAdded}
          />
        )}
        {step === 2 && (
          <StepReglas
            form={form}
            update={update}
            holidays={catalogues.holidays}
            policies={catalogues.policies}
            onCatalogueAdded={onCatalogueAdded}
            onCreateHolidayDefault={async () => {
              const r = await createHolidayGroup({ name: "Festivos nacionales", kind: "national" });
              const item = unwrap(r);
              if (item?.id) {
                onCatalogueAdded("holidays", item);
                update({ holiday_group_ids: [item.id] });
              }
            }}
          />
        )}
        {step === 3 && <StepDocumentos />}
        {step === 4 && (
          <StepResumen
            form={form}
            employee={selectedEmployee}
            schedule={selectedSchedule}
            holidays={selectedHolidays}
            policy={selectedPolicy}
          />
        )}
      </div>

      <div style={{ padding: "12px 20px", borderTop: "1px solid var(--kr-border)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--kr-neutral-50)" }}>
        <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Paso {step + 1} de {steps.length}</span>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancelar</Button>
          {step > 0 && <Button variant="secondary" icon="ChevronLeft" onClick={() => setStep(step - 1)} disabled={submitting}>Atrás</Button>}
          {step < steps.length - 1 && (
            <Button
              variant="primary"
              iconRight="ChevronRight"
              onClick={() => setStep(step + 1)}
              disabled={!steps[step].isValid()}
            >
              Continuar
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button variant="primary" icon="Check" onClick={submit} disabled={!canSubmit}>
              {submitting ? "Creando…" : "Crear contrato"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FormSection({ title, hint, children, cols = 2 }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{title}</div>
        {hint && <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 14 }}>{children}</div>
    </section>
  );
}

function FieldLabel({ label, children, hint, full }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: full ? "1 / -1" : "auto" }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{hint}</span>}
    </label>
  );
}

function StepEmpleado({ employees, value, onChange }) {
  if (employees.length === 0) {
    return (
      <Alert tone="warning">
        No hay empleados aún. Crea un empleado en Equipo › Empleados antes de asignar un contrato.
      </Alert>
    );
  }
  return (
    <div>
      <FormSection title="Empleado" hint="Selecciona el empleado al que aplica este contrato.">
        <FieldLabel label="Empleado *" full>
          <Select value={value} onChange={(e) => onChange(e.target.value)}>
            <option value="">— Selecciona empleado —</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {fullName(emp)}{emp.position ? ` · ${emp.position}` : ""}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </FormSection>
    </div>
  );
}

function StepPeriodo({ form, update, schedules, selectedSchedule, onCatalogueAdded }) {
  const [showPicker, setShowPicker] = useState(false);
  const weeklyHours = selectedSchedule?.weekly_minutes
    ? (selectedSchedule.weekly_minutes / 60).toFixed(1)
    : null;

  return (
    <div>
      <FormSection title="Periodo del contrato" hint="Si es un contrato indefinido, deja la fecha fin vacía.">
        <FieldLabel label="Fecha de inicio *">
          <Input type="date" value={form.start_date} onChange={(e) => update({ start_date: e.target.value })} />
        </FieldLabel>
        <FieldLabel label="Fecha de fin" hint="Opcional · solo en contratos temporales">
          <Input type="date" value={form.end_date} onChange={(e) => update({ end_date: e.target.value })} />
        </FieldLabel>
      </FormSection>

      <FormSection title="Horario asignado" hint="El horario define las horas semanales y los días laborables.">
        <FieldLabel label="Horario *" full>
          {schedules.length === 0 ? (
            <Alert tone="warning">
              No hay horarios creados. Crea uno en Configuración › Horarios antes de continuar.
            </Alert>
          ) : (
            <div
              style={{
                minHeight: 56,
                padding: 8,
                border: "1px solid var(--kr-border)",
                borderRadius: 6,
                background: "var(--kr-bg-surface)",
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                alignItems: "center",
              }}
            >
              {selectedSchedule ? (
                <SelectedScheduleChip schedule={selectedSchedule} />
              ) : (
                <span
                  style={{
                    color: "var(--kr-fg-muted)",
                    fontSize: 13,
                    padding: "4px 8px",
                    flex: 1,
                  }}
                >
                  Sin horario asignado todavía.
                </span>
              )}
              <Button
                variant="secondary"
                size="sm"
                icon={selectedSchedule ? "Pencil" : "Plus"}
                onClick={() => setShowPicker(true)}
                style={{ marginLeft: selectedSchedule ? "auto" : 0 }}
              >
                {selectedSchedule ? "Cambiar" : "Asignar horario"}
              </Button>
            </div>
          )}
        </FieldLabel>
      </FormSection>

      {selectedSchedule && (
        <div style={{ background: "var(--kr-status-info-bg)", padding: 12, borderRadius: 8, display: "flex", gap: 10 }}>
          <Icon name="AlertTriangle" size={16} style={{ color: "var(--kr-info-700)", flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: "var(--kr-info-700)" }}>
            <strong>Total jornada calculada:</strong> {weeklyHours} h/sem según el horario «{selectedSchedule.name}». Estos valores se usan para detectar horas extras y ausencias.
          </div>
        </div>
      )}

      <WorkSchedulePickerModal
        open={showPicker}
        schedules={schedules}
        initialId={form.work_schedule_id}
        onClose={() => setShowPicker(false)}
        onAssign={(id) => {
          update({ work_schedule_id: id });
          setShowPicker(false);
        }}
        onScheduleCreated={(schedule) => {
          if (onCatalogueAdded) onCatalogueAdded("schedules", schedule);
        }}
      />
    </div>
  );
}

function StepReglas({ form, update, holidays, policies, onCreateHolidayDefault, onCatalogueAdded }) {
  const [creatingHoliday, setCreatingHoliday] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showPolicyPicker, setShowPolicyPicker] = useState(false);

  const handleHolidayDefault = async () => {
    setCreatingHoliday(true);
    setCreateError(null);
    try { await onCreateHolidayDefault(); } catch (e) {
      setCreateError(typeof e === "string" ? e : e?.message || "No se pudo crear el grupo por defecto");
    } finally { setCreatingHoliday(false); }
  };

  const selectedHolidays = holidays.filter((h) => form.holiday_group_ids.includes(h.id));
  const selectedPolicy = policies.find((p) => p.id === form.vacation_policy_id) || null;

  const removeHoliday = (id) => {
    update({ holiday_group_ids: form.holiday_group_ids.filter((x) => x !== id) });
  };

  return (
    <div>
      {createError && <div style={{ marginBottom: 12 }}><Alert tone="danger">{createError}</Alert></div>}

      <FormSection title="Política de vacaciones">
        <FieldLabel label="Política *" full>
          <div
            style={{
              minHeight: 56,
              padding: 8,
              border: "1px solid var(--kr-border)",
              borderRadius: 6,
              background: "var(--kr-bg-surface)",
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            {selectedPolicy ? (
              <SelectedPolicyChip policy={selectedPolicy} />
            ) : (
              <span style={{ color: "var(--kr-fg-muted)", fontSize: 13, padding: "4px 8px", flex: 1 }}>
                Sin política asignada todavía.
              </span>
            )}
            <Button
              variant="secondary"
              size="sm"
              icon={selectedPolicy ? "Pencil" : "Plus"}
              onClick={() => setShowPolicyPicker(true)}
              style={{ marginLeft: selectedPolicy ? "auto" : 0 }}
            >
              {selectedPolicy ? "Cambiar" : "Asignar política"}
            </Button>
          </div>
        </FieldLabel>
      </FormSection>

      <VacationPolicyPickerModal
        open={showPolicyPicker}
        policies={policies}
        initialId={form.vacation_policy_id}
        onClose={() => setShowPolicyPicker(false)}
        onAssign={(id) => {
          update({ vacation_policy_id: id });
          setShowPolicyPicker(false);
        }}
        onPolicyCreated={(policy) => {
          if (onCatalogueAdded) onCatalogueAdded("policies", policy);
        }}
      />

      <FormSection
        title="Grupos de festivos"
        hint="Asigna al menos uno. Suele combinarse nacional + autonómico + local del lugar de trabajo."
      >
        <FieldLabel label="Festivos *" full>
          {holidays.length === 0 ? (
            <Button variant="secondary" icon="Plus" onClick={handleHolidayDefault} disabled={creatingHoliday}>
              {creatingHoliday ? "Creando…" : "Crear grupo «Festivos nacionales»"}
            </Button>
          ) : (
            <div
              style={{
                minHeight: 56,
                padding: 8,
                border: "1px solid var(--kr-border)",
                borderRadius: 6,
                background: "var(--kr-bg-surface)",
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                alignItems: "center",
              }}
            >
              {selectedHolidays.length === 0 ? (
                <span style={{ color: "var(--kr-fg-muted)", fontSize: 13, padding: "4px 8px", flex: 1 }}>
                  Sin grupos asignados todavía.
                </span>
              ) : (
                selectedHolidays.map((h) => (
                  <SelectedHolidayChip
                    key={h.id}
                    group={h}
                    onRemove={() => removeHoliday(h.id)}
                  />
                ))
              )}
              <Button
                variant="secondary"
                size="sm"
                icon="Plus"
                onClick={() => setShowPicker(true)}
                style={{ marginLeft: selectedHolidays.length === 0 ? 0 : "auto" }}
              >
                {selectedHolidays.length === 0 ? "Añadir festivos" : "Editar"}
              </Button>
            </div>
          )}
        </FieldLabel>
        {form.holiday_group_ids.length === 0 && holidays.length > 0 && (
          <div style={{ gridColumn: "1 / -1" }}>
            <Alert tone="warning">
              Selecciona al menos un grupo de festivos. Por defecto se recomienda el nacional.
            </Alert>
          </div>
        )}
      </FormSection>

      <HolidayGroupsPickerModal
        open={showPicker}
        holidays={holidays}
        initialIds={form.holiday_group_ids}
        onClose={() => setShowPicker(false)}
        onAssign={(ids) => {
          update({ holiday_group_ids: ids });
          setShowPicker(false);
        }}
        onGroupCreated={(group) => {
          if (onCatalogueAdded) onCatalogueAdded("holidays", group);
        }}
      />

      <FormSection title="Datos del puesto" hint="Opcionales; sirven para los reportes y la nómina.">
        <FieldLabel label="Categoría / puesto">
          <Input value={form.job_title} onChange={(e) => update({ job_title: e.target.value })} placeholder="Ej. Diseñadora UX" />
        </FieldLabel>
        <FieldLabel label="Salario bruto anual">
          <div style={{ display: "flex", gap: 6 }}>
            <Input
              type="number"
              value={form.salary_amount}
              onChange={(e) => update({ salary_amount: e.target.value })}
              placeholder="0"
              style={{ flex: 1 }}
            />
            <Select
              value={form.salary_currency}
              onChange={(e) => update({ salary_currency: e.target.value })}
              style={{ width: 96 }}
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </Select>
          </div>
        </FieldLabel>
      </FormSection>
    </div>
  );
}

function StepDocumentos() {
  return (
    <div>
      <FormSection title="Documentación" hint="La carga de documentos se habilitará en la siguiente iteración." cols={1}>
        <div style={{ border: "2px dashed var(--kr-border)", borderRadius: 8, padding: 32, textAlign: "center", background: "var(--kr-neutral-50)" }}>
          <Icon name="Upload" size={20} style={{ color: "var(--kr-fg-muted)" }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)", marginTop: 8 }}>Próximamente</div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4 }}>Adjuntar contrato firmado y anexos en PDF</div>
        </div>
      </FormSection>
    </div>
  );
}

function StepResumen({ form, employee, schedule, holidays, policy }) {
  const weekly = schedule?.weekly_minutes ? (schedule.weekly_minutes / 60).toFixed(1) : "—";
  const holidaysLabel = holidays && holidays.length > 0
    ? holidays.map((h) => h.name).join(" · ")
    : "—";
  const empleadoRows = [
    ["Empleado", fullName(employee)],
    ["Puesto", employee?.position || "—"],
  ];
  const periodoRows = [
    ["Inicio", fmtDate(form.start_date) || "—"],
    ["Fin", form.end_date ? fmtDate(form.end_date) : "Indefinido"],
  ];
  const jornadaRows = [
    ["Horario", schedule?.name || "—"],
    ["h/sem", weekly],
  ];
  const reglasRows = [
    ["Vacaciones", policy?.name || "—"],
    ["Festivos", holidaysLabel],
    ["Categoría", form.job_title || "—"],
    ["Salario", form.salary_amount !== "" ? `${form.salary_amount} ${form.salary_currency || ""}` : "—"],
  ];

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 4 }}>Resumen del contrato</div>
      <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginBottom: 16 }}>
        Revisa las condiciones antes de crear el contrato.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <SummaryBlock title="Empleado" rows={empleadoRows} />
        <SummaryBlock title="Periodo" rows={periodoRows} />
        <SummaryBlock title="Jornada" rows={jornadaRows} />
        <SummaryBlock title="Reglas" rows={reglasRows} />
      </div>
    </div>
  );
}

function SelectedHolidayChip({ group, onRemove }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 4px 4px 10px",
        background: "var(--kr-action-soft-bg)",
        color: "var(--kr-action-soft-fg)",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      <span>{group.name}</span>
      <span
        style={{
          fontSize: 10,
          color: "var(--kr-fg-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        · {KIND_LABEL[group.kind] || group.kind}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Quitar grupo"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 18,
          height: 18,
          borderRadius: 999,
          border: "none",
          background: "transparent",
          color: "var(--kr-fg-muted)",
          cursor: "pointer",
        }}
      >
        <Icon name="X" size={12} />
      </button>
    </span>
  );
}

function HolidayGroupsPickerModal({ open, holidays, initialIds, onClose, onAssign, onGroupCreated }) {
  const [selected, setSelected] = useState(() => new Set(initialIds || []));
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(new Set(initialIds || []));
      setFilter("all");
      setSearch("");
    }
  }, [open, initialIds]);

  const filterTabs = useMemo(
    () => [
      { id: "all", label: "Todos", count: holidays.length },
      { id: "national", label: "Nacionales", count: holidays.filter((h) => h.kind === "national").length },
      { id: "regional", label: "Autonómicos", count: holidays.filter((h) => h.kind === "regional").length },
      { id: "local", label: "Locales", count: holidays.filter((h) => h.kind === "local").length },
      { id: "other", label: "Otros", count: holidays.filter((h) => h.kind === "other").length },
    ],
    [holidays],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return holidays.filter((h) => {
      if (filter !== "all" && h.kind !== filter) return false;
      if (q && !(h.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [holidays, filter, search]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
    <Modal open={open} onClose={onClose} title="Seleccionar grupos de festivos" size="lg">
      <ModalBody>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre…"
          icon="Search"
          style={{ marginBottom: 12 }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 14,
          }}
        >
          {filterTabs.map((t) => {
            if (t.count === 0 && t.id !== "all") return null;
            const active = filter === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setFilter(t.id)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  height: 28,
                  padding: "0 10px",
                  fontSize: 12,
                  fontWeight: 500,
                  border: `1px solid ${active ? "var(--kr-action)" : "var(--kr-border)"}`,
                  background: active ? "var(--kr-action-soft-bg)" : "#fff",
                  color: active ? "var(--kr-action-soft-fg)" : "var(--kr-fg-strong)",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                {t.label}
                <span style={{ fontSize: 10, opacity: 0.7, fontFamily: "var(--kr-font-mono)" }}>
                  {t.count}
                </span>
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <Button
            variant="ghost"
            size="sm"
            icon="Plus"
            onClick={() => setShowCreate(true)}
          >
            Crear nuevo grupo
          </Button>
        </div>

        {visible.length === 0 ? (
          <EmptyState
            icon="Calendar"
            title={search.trim() ? "Sin resultados" : "Sin grupos"}
            description={
              search.trim()
                ? "Prueba con otro nombre o cambia el filtro."
                : "Crea grupos en Configuración › Festivos antes de asignarlos a un contrato."
            }
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              maxHeight: 360,
              overflowY: "auto",
            }}
          >
            {visible.map((h) => {
              const checked = selected.has(h.id);
              return (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => toggle(h.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    border: `1px solid ${checked ? "var(--kr-action)" : "var(--kr-border-subtle)"}`,
                    background: checked ? "var(--kr-action-soft-bg)" : "#fff",
                    borderRadius: 8,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: `1.5px solid ${checked ? "var(--kr-action)" : "var(--kr-border)"}`,
                      background: checked ? "var(--kr-action)" : "transparent",
                      color: "#fff",
                      flexShrink: 0,
                    }}
                  >
                    {checked && <Icon name="Check" size={12} />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "var(--kr-fg-strong)",
                      }}
                    >
                      {h.name}
                    </div>
                  </div>
                  <Badge tone={KIND_TONE[h.kind] || "neutral"}>
                    {KIND_LABEL[h.kind] || h.kind}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={() => onAssign(Array.from(selected))}
          disabled={selected.size === 0}
        >
          Asignar
          {selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
      </ModalFooter>
    </Modal>

    <CreateHolidayGroupInlineModal
      open={showCreate}
      onClose={() => setShowCreate(false)}
      onCreated={(group) => {
        if (onGroupCreated) onGroupCreated(group);
        setSelected((s) => new Set([...s, group.id]));
        setShowCreate(false);
      }}
    />
    </>
  );
}

function CreateHolidayGroupInlineModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [kind, setKind] = useState("national");
  const [days, setDays] = useState([{ date: "", name: "" }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setName("");
      setKind("national");
      setDays([{ date: "", name: "" }]);
      setError(null);
    }
  }, [open]);

  const addDay = () => setDays((d) => [...d, { date: "", name: "" }]);
  const removeDay = (i) => setDays((d) => d.filter((_, idx) => idx !== i));
  const updateDay = (i, patch) =>
    setDays((d) => d.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const cleanDays = () =>
    days
      .map((d) => ({ date: d.date.trim(), name: d.name.trim() }))
      .filter((d) => d.date && d.name);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const groupRes = await createHolidayGroup({ name: name.trim(), kind });
      const group = unwrap(groupRes);
      if (!group?.id) throw new Error("No se pudo crear el grupo.");

      const valid = cleanDays();
      let okCount = 0;
      let failCount = 0;
      if (valid.length > 0) {
        const results = await Promise.allSettled(
          valid.map((d) => createHoliday(group.id, d)),
        );
        okCount = results.filter((r) => r.status === "fulfilled").length;
        failCount = results.length - okCount;
      }

      onCreated(group);

      if (failCount > 0) {
        toast.warning(
          "Grupo creado con avisos",
          `${okCount} de ${valid.length} festivos añadidos. Revisa fechas duplicadas o inválidas.`,
        );
      } else if (okCount > 0) {
        toast.success("Grupo creado", `${group.name} con ${okCount} festivos.`);
      } else {
        toast.success("Grupo creado", `${group.name} sin festivos todavía.`);
      }
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo crear el grupo.");
    } finally {
      setSaving(false);
    }
  };

  const validDays = cleanDays().length;
  const canSubmit = name.trim().length > 0 && !saving;

  return (
    <Modal open={open} onClose={onClose} title="Crear nuevo grupo de festivos" size="lg">
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 14 }}>
            <FieldLabel label="Nombre del grupo *">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                required
                autoFocus
                placeholder="Ej. Festivos Madrid 2026"
              />
            </FieldLabel>
            <FieldLabel label="Tipo *">
              <Select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="national">Nacional</option>
                <option value="regional">Autonómico</option>
                <option value="local">Local</option>
                <option value="other">Otro</option>
              </Select>
            </FieldLabel>
          </div>

          <div style={{ marginTop: 18 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--kr-fg-strong)",
                }}
              >
                Festivos del grupo
              </div>
              <Button variant="ghost" size="sm" icon="Plus" onClick={addDay}>
                Añadir día
              </Button>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxHeight: 280,
                overflowY: "auto",
              }}
            >
              {days.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "160px 1fr 36px",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <Input
                    type="date"
                    value={d.date}
                    onChange={(e) => updateDay(i, { date: e.target.value })}
                  />
                  <Input
                    value={d.name}
                    onChange={(e) => updateDay(i, { name: e.target.value })}
                    placeholder="Nombre del festivo"
                    maxLength={255}
                  />
                  <IconBtn
                    icon="Trash2"
                    onClick={() => removeDay(i)}
                    title="Quitar"
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 8 }}>
              Las filas vacías se ignoran. Puedes añadir más festivos después de crear el grupo.
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={!canSubmit}>
            {saving && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
            {saving
              ? "Creando…"
              : validDays > 0
                ? `Crear y asignar (${validDays} festivos)`
                : "Crear y asignar"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABEL = {
  mon: "Lunes",
  tue: "Martes",
  wed: "Miércoles",
  thu: "Jueves",
  fri: "Viernes",
  sat: "Sábado",
  sun: "Domingo",
};

function formatMinutes(mins) {
  if (!mins || mins <= 0) return "0h";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function SelectedScheduleChip({ schedule }) {
  const weekly = schedule.weekly_minutes
    ? `${(schedule.weekly_minutes / 60).toFixed(1)} h/sem`
    : null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        background: "var(--kr-action-soft-bg)",
        color: "var(--kr-action-soft-fg)",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      <Icon name="Clock" size={12} />
      <span>{schedule.name}</span>
      {weekly && (
        <span
          style={{
            fontSize: 10,
            color: "var(--kr-fg-muted)",
            fontFamily: "var(--kr-font-mono)",
          }}
        >
          · {weekly}
        </span>
      )}
    </span>
  );
}

function WorkSchedulePickerModal({ open, schedules, initialId, onClose, onAssign, onScheduleCreated }) {
  const [selectedId, setSelectedId] = useState(initialId || (schedules[0]?.id ?? ""));
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedId(initialId || (schedules[0]?.id ?? ""));
      setSearch("");
    }
  }, [open, initialId, schedules]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return schedules;
    return schedules.filter((s) => (s.name || "").toLowerCase().includes(q));
  }, [schedules, search]);

  const selected = useMemo(
    () => schedules.find((s) => s.id === selectedId) || null,
    [schedules, selectedId],
  );

  return (
    <>
    <Modal open={open} onClose={onClose} title="Asignar horario" size="xl">
      <ModalBody>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "260px 1fr",
            gap: 0,
            border: "1px solid var(--kr-border-subtle)",
            borderRadius: 8,
            overflow: "hidden",
            height: 440,
          }}
        >
          <div
            style={{
              borderRight: "1px solid var(--kr-border-subtle)",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <div style={{ padding: 8, borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", flexDirection: "column", gap: 6 }}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar horario…"
                icon="Search"
                size="sm"
              />
              <Button
                variant="ghost"
                size="sm"
                icon="Plus"
                onClick={() => setShowCreate(true)}
                style={{ justifyContent: "flex-start" }}
              >
                Crear nuevo horario
              </Button>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filtered.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    textAlign: "center",
                    color: "var(--kr-fg-muted)",
                    fontSize: 12,
                  }}
                >
                  Sin resultados.
                </div>
              ) : (
                filtered.map((s) => {
                  const active = s.id === selectedId;
                  const weekly = s.weekly_minutes
                    ? `${(s.weekly_minutes / 60).toFixed(1)} h/sem`
                    : null;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelectedId(s.id)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 14px",
                        border: "none",
                        borderLeft: active
                          ? "3px solid var(--kr-action)"
                          : "3px solid transparent",
                        borderBottom: "1px solid var(--kr-border-subtle)",
                        background: active ? "var(--kr-action-soft-bg)" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: active ? 600 : 500,
                          color: "var(--kr-fg-strong)",
                        }}
                      >
                        {s.name}
                      </div>
                      {weekly && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--kr-fg-muted)",
                            fontFamily: "var(--kr-font-mono)",
                            marginTop: 2,
                          }}
                        >
                          {weekly}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div style={{ overflowY: "auto", padding: 16 }}>
            {selected ? (
              <ScheduleDetail schedule={selected} />
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--kr-fg-muted)",
                  fontSize: 13,
                }}
              >
                Selecciona un horario para ver el detalle.
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={() => onAssign(selectedId)}
          disabled={!selectedId}
        >
          Asignar
        </Button>
      </ModalFooter>
    </Modal>

    <CreateWorkScheduleInlineModal
      open={showCreate}
      onClose={() => setShowCreate(false)}
      onCreated={(schedule) => {
        if (onScheduleCreated) onScheduleCreated(schedule);
        setSelectedId(schedule.id);
        setShowCreate(false);
      }}
    />
    </>
  );
}

function CreateWorkScheduleInlineModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [config, setConfig] = useState(() => buildEmptyConfig());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setName("");
      setConfig(buildEmptyConfig());
      setError(null);
    }
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { name: name.trim(), ...buildConfigPayload(config) };
      const r = await createWorkSchedule(payload);
      const created = unwrap(r);
      if (!created?.id) throw new Error("No se pudo crear el horario.");
      onCreated(created);
      toast.success("Horario creado", `${created.name} guardado.`);
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo crear el horario.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Crear nuevo horario" size="xl">
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          <FieldLabel label="Nombre del horario *" full>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              required
              autoFocus
              placeholder="Ej. Oficina 40 h"
            />
          </FieldLabel>

          <div style={{ marginTop: 16 }}>
            <WorkScheduleEditor value={config} onChange={setConfig} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving || !name.trim()}>
            {saving && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
            {saving ? "Creando…" : "Crear y asignar"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function ScheduleDetail({ schedule }) {
  const days = schedule.config?.days || {};
  const tz = schedule.config?.timezone;
  const weeklyHours = schedule.weekly_minutes
    ? (schedule.weekly_minutes / 60).toFixed(1)
    : "0";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "var(--kr-fg-strong)",
            }}
          >
            {schedule.name}
          </div>
          {tz && (
            <div
              style={{
                fontSize: 11,
                color: "var(--kr-fg-muted)",
                marginTop: 2,
              }}
            >
              Zona horaria: {tz}
            </div>
          )}
        </div>
        <div
          style={{
            fontFamily: "var(--kr-font-mono)",
            fontSize: 13,
            color: "var(--kr-fg-strong)",
            fontWeight: 600,
          }}
        >
          {weeklyHours} h/sem
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          border: "1px solid var(--kr-border-subtle)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1.4fr 1fr 1fr",
            padding: "8px 12px",
            background: "var(--kr-neutral-50)",
            borderBottom: "1px solid var(--kr-border)",
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 600,
            color: "var(--kr-fg-muted)",
          }}
        >
          <span>Día</span>
          <span>Horario</span>
          <span>Pausa</span>
          <span style={{ textAlign: "right" }}>Total</span>
        </div>
        {DAY_KEYS.map((key, i) => {
          const day = days[key] || {};
          const totalMin = Number(day.total_minutes ?? 0);
          const isWorking = totalMin > 0;
          const breakMin = Number(day.break?.minutes ?? 0);
          return (
            <div
              key={key}
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 1.4fr 1fr 1fr",
                padding: "10px 12px",
                borderBottom: i === DAY_KEYS.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
                fontSize: 13,
                color: isWorking ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
              }}
            >
              <span style={{ fontWeight: 500 }}>{DAY_LABEL[key]}</span>
              <span
                style={{
                  fontFamily: "var(--kr-font-mono)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {isWorking ? `${day.start || "—"} – ${day.end || "—"}` : "No laborable"}
              </span>
              <span style={{ fontFamily: "var(--kr-font-mono)" }}>
                {isWorking ? (breakMin > 0 ? `${breakMin} min` : "—") : ""}
              </span>
              <span
                style={{
                  fontFamily: "var(--kr-font-mono)",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  textAlign: "right",
                }}
              >
                {isWorking ? formatMinutes(totalMin) : ""}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SelectedPolicyChip({ policy }) {
  const summary = summarizePolicy(policy);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        background: "var(--kr-action-soft-bg)",
        color: "var(--kr-action-soft-fg)",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      <Icon name="Calendar" size={12} />
      <span>{policy.name}</span>
      <span
        style={{
          fontSize: 10,
          color: "var(--kr-fg-muted)",
          fontFamily: "var(--kr-font-mono)",
        }}
      >
        · {summary}
      </span>
    </span>
  );
}

function VacationPolicyPickerModal({ open, policies, initialId, onClose, onAssign, onPolicyCreated }) {
  const [selectedId, setSelectedId] = useState(initialId || (policies[0]?.id ?? ""));
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedId(initialId || (policies[0]?.id ?? ""));
      setSearch("");
    }
  }, [open, initialId, policies]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return policies;
    return policies.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [policies, search]);

  const selected = useMemo(
    () => policies.find((p) => p.id === selectedId) || null,
    [policies, selectedId],
  );

  return (
    <>
    <Modal open={open} onClose={onClose} title="Asignar política de vacaciones" size="xl">
      <ModalBody>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "280px 1fr",
            gap: 0,
            border: "1px solid var(--kr-border-subtle)",
            borderRadius: 8,
            overflow: "hidden",
            height: 460,
          }}
        >
          <div
            style={{
              borderRight: "1px solid var(--kr-border-subtle)",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <div style={{ padding: 8, borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", flexDirection: "column", gap: 6 }}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar política…"
                icon="Search"
                size="sm"
              />
              <Button
                variant="ghost"
                size="sm"
                icon="Plus"
                onClick={() => setShowCreate(true)}
                style={{ justifyContent: "flex-start" }}
              >
                Crear nueva política
              </Button>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filtered.length === 0 ? (
                <div style={{ padding: 16, textAlign: "center", color: "var(--kr-fg-muted)", fontSize: 12 }}>
                  Sin resultados.
                </div>
              ) : (
                filtered.map((p) => {
                  const active = p.id === selectedId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 14px",
                        border: "none",
                        borderLeft: active ? "3px solid var(--kr-action)" : "3px solid transparent",
                        borderBottom: "1px solid var(--kr-border-subtle)",
                        background: active ? "var(--kr-action-soft-bg)" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: "var(--kr-fg-strong)" }}>
                        {p.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--kr-fg-muted)",
                          fontFamily: "var(--kr-font-mono)",
                          marginTop: 2,
                        }}
                      >
                        {summarizePolicy(p)}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div style={{ overflowY: "auto", padding: 16 }}>
            {selected ? (
              <VacationPolicyDetail policy={selected} />
            ) : (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--kr-fg-muted)", fontSize: 13 }}>
                Selecciona una política para ver el detalle.
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" type="button" onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          type="button"
          onClick={() => onAssign(selectedId)}
          disabled={!selectedId}
        >
          Asignar
        </Button>
      </ModalFooter>
    </Modal>

    <VacationPolicyEditorModal
      open={showCreate}
      policy={null}
      onClose={() => setShowCreate(false)}
      onSaved={(saved) => {
        if (onPolicyCreated) onPolicyCreated(saved);
        setSelectedId(saved.id);
        setShowCreate(false);
      }}
    />
    </>
  );
}

const labelOf = (list, value) => list.find((o) => o.value === value)?.label || value || "—";

function VacationPolicyDetail({ policy }) {
  const lines = [
    ["Días base", policy.unlimited
      ? "Ilimitados"
      : `${Number(policy.allowance_per_year || 0)} ${labelOf(VAC_ACCRUAL_UNITS, policy.accrual_type).toLowerCase()} ${labelOf(VAC_DAY_COUNT_BASES, policy.day_count_basis).toLowerCase()}`,
    ],
    ["Ciclo", `${labelOf(VAC_MONTHS, policy.cycle_start_month)} · ${policy.cycle_duration_months} meses`],
    ["Generación", labelOf(VAC_ACCRUAL_MODES, policy.accrual_mode)],
    ["Festivo en ausencia",
      policy.holiday_inside_absence_counts ? "Cuenta como día consumido" : "No cuenta",
    ],
    ["Prorrateo primer ciclo", policy.prorate_first_year ? "Sí" : "No"],
    ["Saldo negativo", policy.allow_negative_balance ? "Permitido" : "No permitido"],
    ["Usar en ciclo siguiente", policy.allow_usage_in_next_cycle ? "Sí" : "No"],
    ["Máximo del contador", policy.max_balance ? `${Number(policy.max_balance)}` : "Sin límite"],
    ["Antelación mínima", policy.min_notice_days > 0 ? `${policy.min_notice_days} días` : "Ninguna"],
    ["Acumulación",
      policy.carryover_unlimited
        ? "Ilimitada"
        : Number(policy.carryover_amount) > 0
          ? `${Number(policy.carryover_amount)}${policy.carryover_expiration_months ? ` · vencen tras ${policy.carryover_expiration_months}m` : " · sin vencimiento"}`
          : "Sin arrastre",
    ],
  ];

  const tenure = Array.isArray(policy.tenure_periods) ? policy.tenure_periods : [];

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
        {policy.name}
      </div>

      <div
        style={{
          marginTop: 14,
          border: "1px solid var(--kr-border-subtle)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {lines.map(([label, value], i) => (
          <div
            key={label}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.4fr",
              padding: "8px 14px",
              borderBottom: i === lines.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
              fontSize: 13,
            }}
          >
            <span style={{ color: "var(--kr-fg-muted)" }}>{label}</span>
            <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>{value}</span>
          </div>
        ))}
      </div>

      {tenure.length > 0 && (
        <>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
              marginTop: 16,
              marginBottom: 8,
            }}
          >
            Períodos de antigüedad
          </div>
          <div
            style={{
              border: "1px solid var(--kr-border-subtle)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {tenure.map((t, i) => (
              <div
                key={t.id || i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  padding: "8px 14px",
                  borderBottom: i === tenure.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
                  fontSize: 13,
                }}
              >
                <span style={{ color: "var(--kr-fg-muted)" }}>
                  Tras {t.years_of_service} año{t.years_of_service > 1 ? "s" : ""}
                </span>
                <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>
                  +{Number(t.extra_amount)} {t.unit === "hours" ? "horas" : "días"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Contratos;
